/**
 * Typed in-app event bus. Replaces ad-hoc window.dispatchEvent to avoid duplicate
 * events and to namespace events. Use for refresh, realtime, and app lifecycle.
 */

type DetailMap = {
  'app:refreshPageData': { page?: string; childId?: string; householdId?: string }
  'app:daily-completion-updated': { child_id?: string; [key: string]: unknown }
  /** `deleted` : la ligne a été supprimée (annulation), les autres champs sont ceux d'avant. */
  'app:task-submission-changed': { child_id?: string; task_id?: string; status?: string; deleted?: boolean; [key: string]: unknown }
  'app:childRecovered': { childId?: string; child?: unknown }
  'app:app-resumed': void
  'app:pull-to-refresh': void
  // Triple-tap on the Home tab reveals the screenshot dev panel (screenshot-* accounts only).
  'app:screenshot-reveal': void
  // Screenshot orchestration (screenshot-* accounts only): open the AvatarAlbum
  // customization modal over the current Tasks page.
  'app:screenshot-open-avatar-lobby': void
  // Scroll-driven scrim progress (0..1) bubbled from a routed child-mode tab up
  // to ChildModeTabsPage where the AppTopBar lives (router outlet blocks props).
  // `hidden` porte le second usage : la barre se range vers le haut quand on
  // descend. Facultatif, les émetteurs qui ne mesurent que le voile l'omettent.
  'app:scrim-progress': { progress: number; hidden?: boolean }
  // Une page demande le calendrier du mois. Il vit dans la barre du haut, qui
  // doit survivre à un changement d'onglet, et le router-view ne laisse pas
  // passer de props.
  'app:open-streak-calendar': void
  // Une page demande l'accès mobile de l'enfant. Le tiroir et son mur
  // Harmonia+ vivent dans la barre du haut, avec les autres actions par enfant.
  // `childId` : l'Accueil n'a pas d'enfant en contexte, il nomme le sien.
  'app:open-mobile-access': { childId?: string } | undefined
  // Le mode enfant demandé depuis une page (#402) : la carte de découverte de
  // la page des tâches. `action` : entrer sur cet appareil, ou appairer le
  // téléphone de l'enfant. La barre du haut s'en charge, paywall compris.
  'app:child-mode': { childId?: string; action: 'here' | 'phone' }
  // Ouvre la feuille d'encouragement (parent) pour un enfant, depuis n'importe
  // quel écran du parent : toast de validation, journée terminée, menu enfant.
  'app:open-cheer': { childId: string; taskId?: string | null; taskTitle?: string | null }
  // Une pièce part d'un point de l'écran vers le compteur de pièces visible
  // (cf. lib/coinFlight). Coordonnées du centre de départ, en px de fenêtre.
  'app:coin-flight': { x: number; y: number }
  // Les nouveautés de la version, rouvertes à la demande (Réglages). La
  // feuille vit dans App.vue (WhatsNewSheet), montée une seule fois.
  'app:open-whats-new': void
}

type EventName = keyof DetailMap

// Dédoublonnage « premier gagnant » : les répétitions dans la fenêtre sont jetées.
const DEDUP_MS: Partial<Record<EventName, number>> = {
  'app:refreshPageData': 300,
  'app:app-resumed': 1000,
  'app:pull-to-refresh': 800
}

// Regroupement « dernier gagnant » : les émissions d'une même clé sont retenues
// et seule la dernière part, une fois la fenêtre écoulée sans nouvelle émission.
// Une validation écrit daily_completions deux ou trois fois de suite ; chaque
// écho realtime relançait une vague de relectures, et avec le premier-gagnant
// cette vague partait sur une écriture intermédiaire pendant que l'écho de la
// dernière était jeté (HARMONIA-77).
const TRAILING_MS: Partial<Record<EventName, number>> = {
  'app:daily-completion-updated': 400
}

const LEGACY_NAMES: Partial<Record<EventName, string>> = {
  'app:refreshPageData': 'refreshPageData',
  'app:daily-completion-updated': 'daily-completion-updated',
  'app:childRecovered': 'childRecovered',
  'app:app-resumed': 'app-resumed',
  'app:pull-to-refresh': 'pull-to-refresh'
}

const listeners = new Map<EventName, Set<(detail: any) => void>>()
const lastEmit: Partial<Record<EventName, { key: string; t: number }>> = {}
const trailingTimers = new Map<string, ReturnType<typeof setTimeout>>()

function dedupKey(name: EventName, detail: any): string {
  if (name === 'app:daily-completion-updated' && detail?.child_id) return `${name}:${detail.child_id}:${detail.date ?? ''}`
  if (name === 'app:refreshPageData' && detail?.childId) return `${name}:${detail.page}:${detail.childId}`
  return `${name}:${JSON.stringify(detail ?? {})}`
}

function dispatch<K extends EventName>(name: K, detail: DetailMap[K]): void {
  const set = listeners.get(name)
  if (set) set.forEach((fn) => { try { fn(detail) } catch (e) { console.error('[eventBus]', e) } })
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(name, { detail }))
    const legacy = LEGACY_NAMES[name]
    if (legacy) window.dispatchEvent(new CustomEvent(legacy, { detail }))
  }
}

export const eventBus = {
  emit<K extends EventName>(name: K, detail: DetailMap[K]): void {
    const trailing = TRAILING_MS[name]
    if (trailing) {
      const key = dedupKey(name, detail)
      const pending = trailingTimers.get(key)
      if (pending) clearTimeout(pending)
      trailingTimers.set(key, setTimeout(() => {
        trailingTimers.delete(key)
        dispatch(name, detail)
      }, trailing))
      return
    }
    const dedup = DEDUP_MS[name]
    if (dedup) {
      const key = dedupKey(name, detail)
      const last = lastEmit[name]
      if (last && last.key === key && Date.now() - last.t < dedup) return
      lastEmit[name] = { key, t: Date.now() }
    }
    dispatch(name, detail)
  },

  on<K extends EventName>(name: K, fn: (detail: DetailMap[K]) => void): () => void {
    if (!listeners.has(name)) listeners.set(name, new Set())
    listeners.get(name)!.add(fn as (d: any) => void)
    return () => listeners.get(name)?.delete(fn as (d: any) => void)
  }
}
